import { fn } from '@ember/helper';
import { action, set } from '@ember/object';
import type Owner from '@ember/owner';
import { getOwner } from '@ember/owner';
import { service } from '@ember/service';
import { cached, tracked } from '@glimmer/tracking';
import { PrismButton, PrismComboSelectInput } from '@mylighthouse/prism-ember';
import { t } from 'ember-intl';
import { and } from 'ember-truth-helpers';
import Countries from 'frontend-utils/utils/countries';
import GeneralDropdownsOtaDropdown from 'lumen/components/general/dropdowns/ota-dropdown';
import can from 'lumen/helpers/can';
import type { HotelAndMarketServiceWithHotel } from 'lumen/services/hotel-and-market';

interface HolidayCountry {
  iconFlag: string;
  id: string;
  label: string;
}

interface HotelEventsHolidayConfigurationSignature {
  Args: {
    resetEvents: () => void;
  };
}

// @ts-expect-error: Incorrect type
export default class HotelEventsHolidayConfiguration extends GeneralDropdownsOtaDropdown<HotelEventsHolidayConfigurationSignature> {
  @service('hotel-and-market')
  declare hotelAndMarketService: HotelAndMarketServiceWithHotel;

  @tracked currentCountries: HolidayCountry[] = [];

  private countries: Countries = new Countries(getOwner(this)!);

  dropdownClass = '.dropdown-holiday-configuration';

  @cached
  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get allCountries() {
    return this.countries.list.map((country) => ({
      id: country.id.toLowerCase(),
      label: country.name,
      iconFlag: country.id.toLowerCase(),
    }));
  }

  get holidayCountries(): string[] {
    return this.hotelAndMarketService.hotel.holidayCountries
      .slice(0, 10)
      .map((country) => country.toLowerCase());
  }

  constructor(
    owner: Owner,
    args: HotelEventsHolidayConfigurationSignature['Args'],
  ) {
    // eslint-disable-next-line @typescript-eslint/no-unsafe-call
    super(owner, args);
    this.setCurrentCountries();
  }

  setCurrentCountries(): void {
    const { holidayCountries } = this.hotelAndMarketService.hotel;
    const countryIds = holidayCountries.map((country) => country.toLowerCase());
    this.currentCountries = this.allCountries.filter((country) =>
      countryIds.includes(country.id),
    );
  }

  @action
  saveCountries(): void {
    const currentCountryIds = this.currentCountries.map(
      (country) => country.id,
    );

    const hotel = this.hotelAndMarketService.hotel;
    set(hotel, 'holidayCountries', currentCountryIds);
    this.toggleDropdown();
    // eslint-disable-next-line @typescript-eslint/no-floating-promises
    hotel.save().then((/* newHolidays */) => {
      // @ts-expect-error: Incorrect type
      // eslint-disable-next-line @typescript-eslint/no-unsafe-call, @typescript-eslint/no-unsafe-member-access
      this.args.resetEvents();
    });
  }

  @action
  toggleDropdown(): void {
    // eslint-disable-next-line @typescript-eslint/no-unsafe-call, @typescript-eslint/no-unsafe-member-access
    super.toggleDropdown();
    this.setCurrentCountries();
  }

  <template>
    <div class="holiday-config-container">
      <PrismButton
        @buttonType="secondary"
        @iconAfter={{if
          (can "events_holidays.author")
          (if this.isOpen "chevron-up" "chevron-down")
        }}
        @label={{t
          "events.holiday_configuration.trigger"
          count=this.currentCountries.length
        }}
        @onClick={{this.toggleDropdown}}
      />
      {{#if (and this.isOpen (can "events_holidays.author"))}}
        <div class="dropdown-holiday-configuration rounded-dropdown">
          <div class="country-selection">
            <PrismComboSelectInput
              @isSearchable={{true}}
              @onChange={{fn (mut this.currentCountries)}}
              @options={{this.allCountries}}
              @placeholder={{t "events.select_countries"}}
              @unsafe_isMultipleImmediate={{true}}
              @unsafe_renderInPlace={{true}}
              @value={{this.currentCountries}}
            />
          </div>
          <div class="flex flex-col p-200">
            <PrismButton
              @buttonType="secondary"
              @label={{t "general.save"}}
              @onClick={{this.saveCountries}}
            />
          </div>
        </div>
      {{/if}}
    </div>
  </template>
}
